import React, { useState, useRef, useEffect } from "react";
import "./card.css";
import {
  Box,
  Button,
  Grid,
  Typography,
  Tooltip,
  Zoom,
  Chip,
  ClickAwayListener,
} from "@mui/material";
import { GetTimeString } from "../../utiles";

export interface DeerCardProps {
  uri: any;
  nftData: any;
  name: string;
  assetID: string;
  stakedTime?: any;
  isSelected?: boolean;
  onClick?: any;
  onClaim?: any;
  onStake?: any;
  onUpgrade?: any;
  upgradeTime?: any;
}

const deerTypeStyles = {
  og: {
    backgroundGradient:
      "radial-gradient(circle,rgb(4, 6, 49) 0%,rgb(67, 10, 52) 100%)",
  },
  fawn: {
    backgroundGradient: "radial-gradient(circle, #8B4513 0%, #654321 100%)",
  },
  abyssal: {
    backgroundGradient:
      "radial-gradient(circle, #5c0067 0%,rgb(8, 101, 119) 100%)",
  },
  promo: {
    backgroundGradient:
      "radial-gradient(circle, rgb(97, 93, 55) 0%, rgb(63, 41, 12) 100%)",
  },
  default: {
    backgroundGradient: "radial-gradient(circle, #333333 0%, #111111 100%)",
  },
};

const DeerCard: React.FC<DeerCardProps> = ({
  uri,
  nftData,
  name,
  assetID,
  stakedTime,
  isSelected,
  onClick,
  onClaim = null,
  onStake = null,
  onUpgrade = null,
}) => {
  const deerType =
    nftData?.schema?.schema_name === "og.deer"
      ? "og"
      : nftData?.schema?.schema_name === "fawn"
      ? "fawn"
      : nftData?.schema?.schema_name === "abyssal"
      ? "abyssal"
      : nftData?.schema?.schema_name === "promo"
      ? "promo"
      : "default";

  const currentDeerStyle = deerTypeStyles[deerType] || deerTypeStyles.default;

  const box_style = {
    display: "flex",
    position: "relative",
    justifyContent: "space-between",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    bgcolor: "#5a3c1a",
    borderRadius: 3,
    border: "2px solid " + (isSelected ? "#1fff57" : "transparent"),
    backgroundImage: 'url("/images/card_theme.jpg")',
    boxShadow: isSelected
      ? "0 0 10px #1fff57, 0 0 20px #1fff57"
      : "0 2px 5px rgba(0,0,0,0.3)",
    cursor: "pointer",
    overflow: "hidden",
  };

  const [isHovered, setIsHovered] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const cardRef = useRef(null);

  const [uTime, setUTime] = useState(0);
  const [upgradeTime, setUpgradeTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const upgradeTimeRef = useRef<NodeJS.Timeout | null>(null);

  // Timer for staking countdown
  const startStakedTimer = (
    ref: React.MutableRefObject<NodeJS.Timeout | null>,
    setTime: React.Dispatch<React.SetStateAction<number>>
  ) => {
    if (ref.current == null) {
      ref.current = setInterval(() => {
        setTime((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(ref.current!);
            ref.current = null;
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
  };

  // Timer for upgrade countdown
  const startUpgradeTimer = (
    ref: React.MutableRefObject<NodeJS.Timeout | null>,
    setTime: React.Dispatch<React.SetStateAction<number>>
  ) => {
    if (ref.current == null) {
      ref.current = setInterval(() => {
        setTime((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(ref.current!);
            ref.current = null;
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
  };

  useEffect(() => {
    if (stakedTime && stakedTime > 0) {
      clearInterval(timerRef.current!);
      timerRef.current = null;

      const currentUnixTime = Math.floor(Date.now() / 1000);
      let delayTime = stakedTime - currentUnixTime;

      if (delayTime > 0) {
        setUTime(delayTime);
        startStakedTimer(timerRef, setUTime);
      }
    }

    return () => {
      clearInterval(timerRef.current!);
      timerRef.current = null;
    };
  }, [stakedTime]);

  useEffect(() => {
    if (nftData?.upgrade_end_at && nftData.upgrade_end_at > 0) {
      clearInterval(upgradeTimeRef.current!);
      upgradeTimeRef.current = null;

      const currentUnixTime = Math.floor(Date.now() / 1000);
      let delayTime = nftData.upgrade_end_at - currentUnixTime;

      if (delayTime > 0) {
        setUpgradeTime(delayTime);
        startUpgradeTimer(upgradeTimeRef, setUpgradeTime);
      }
    }

    return () => {
      clearInterval(upgradeTimeRef.current!);
      upgradeTimeRef.current = null;
    };
  }, [nftData?.upgrade_end_at]);

  const onClickNFT = (id: any) => {
    if (onClick && getDeerStatus() === "Idle") {
      onClick(id);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isHovered) {
      timeoutId = setTimeout(() => {
        setTooltipOpen(true);
      }, 500);
    } else {
      setTooltipOpen(false);
    }

    return () => clearTimeout(timeoutId);
  }, [isHovered]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
    setIsHovered(false);
  };

  const renderAttributeItem = (label: string, value: any) => {
    if (!value) return null;
    return (
      <Typography
        variant="body2"
        sx={{
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid #616161",
          pb: 0.5,
          mb: 0.5,
        }}
      >
        <span style={{ color: "#ccc" }}> {label}: </span>
        <span> {value} </span>
      </Typography>
    );
  };

  const getDeerStatus = () => {
    const currentUnixTime = Math.floor(Date.now() / 1000);
    if (!nftData?.stakedConfig) return "Unstaked";
    else if (nftData?.upgrade_end_at > currentUnixTime) {
      return "Upgrading";
    } else if (nftData?.stakedConfig?.current_explore > 0) {
      return "Exploring #" + nftData?.stakedConfig?.current_explore;
    } else if (nftData?.stakedConfig?.last_used + 86400 > currentUnixTime) {
      return "Blocked";
    } else {
      return "Idle";
    }
  };

  const deerStatus = getDeerStatus();
  var rewardMultiplier = nftData?.deer_config?.reward_multi || 1.0;

  const extra_multi = nftData?.deer_config?.level_multi || [];
  const level = nftData?.level || 1;
  if (extra_multi.find((a: any) => a.key == level)) {
    rewardMultiplier +=
      extra_multi.find((a: any) => a.key == level).value / 100 - 1;
  }

  //fix to 2 decimals
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Tooltip
        title={
          <Box>
            {renderAttributeItem("Level", nftData?.level)}
            {renderAttributeItem("Asset ID", nftData?.asset_id)}
            {renderAttributeItem(
              "Type",
              deerType.charAt(0).toUpperCase() + deerType.slice(1)
            )}
            {renderAttributeItem("Rarity", nftData?.data?.Rarity)}
            {renderAttributeItem("Base", nftData?.data?.Base)}
            {renderAttributeItem("Eyes", nftData?.data?.Eyes)}
            {renderAttributeItem("Cloth", nftData?.data?.Cloth)}
            {renderAttributeItem("Horns", nftData?.data?.Horns)}
            {renderAttributeItem("Mouth", nftData?.data?.Mouth)}
            {renderAttributeItem("Wings", nftData?.data?.Wings)}
            {renderAttributeItem("Earring", nftData?.data?.Earring)}
            {renderAttributeItem("Glasses", nftData?.data?.Glasses)}
            {renderAttributeItem("Necklace", nftData?.data?.Necklace)}
            {renderAttributeItem("Background", nftData?.data?.Background)}
          </Box>
        }
        placement="right"
        TransitionComponent={Zoom}
        arrow
        enterDelay={500}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(26, 32, 60, 0.95)",
              color: "white",
              boxShadow: "0 8px 16px rgba(0,0,0,0.4)",
              border: "1px solid rgba(245, 225, 163, 0.2)",
              p: 2,
              maxWidth: 320,
              maxHeight: 400,
              overflowY: "auto",
              zIndex: 9999,
              "& .MuiTooltip-arrow": {
                color: "rgba(26, 32, 60, 0.95)",
              }
            }
          },
          popper: {
            sx: {
              zIndex: 9999
            }
          }
        }}
      >
        <Box
          sx={{
            ...box_style,
            position: 'relative',
            zIndex: 1,
          }}
          ref={cardRef}
          onClick={() => onClickNFT(assetID)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              zIndex: 10,
            }}
          >
            <Box>
              <Chip
                label={deerStatus}
                color={
                  deerStatus === "Upgrading"
                    ? "warning"
                    : deerStatus.includes("Exploring")
                    ? "success"
                    : deerStatus === "Unstaked"
                    ? "error"
                    : "info"
                }
                size="small"
                sx={{ mr: 1, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.7rem", color: "white", textAlign: "right" }}
            >
              {deerStatus.includes("Exploring")
                ? uTime > 0
                  ? GetTimeString(uTime)
                  : "Unclaimed"
                : deerStatus === "Upgrading"
                ? "Leveling"
                : "-"}
            </Typography>
          </Box>

            <Box sx={{ 
              width: "90%", 
              textAlign: "center", 
              m: "auto", 
              mb: 1,
              aspectRatio: "1/1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <img
              style={{
              width: "100%",
              height: "auto",
              maxHeight: "100%",
              borderRadius: "8px",
              objectFit: "contain",
              display: "block",
              }}
              src={uri}
              alt={name}
            />
            </Box>

          <Box sx={{ p: 1, pt: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                mb: 0.5,
                fontSize: "0.85rem",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#ddd",
                textAlign: "center",
                fontStyle: "italic",
                mb: 0.5,
                fontSize: "0.8rem",
              }}
            >
              Level {nftData.level}
            </Typography>
            <Box
              sx={{ display: "flex", justifyContent: "space-around", mt: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#ddd",
                  textAlign: "center",
                  fontStyle: "italic",
                  mb: 0.5,
                  fontSize: "0.7rem",
                }}
              >
                Cost {parseFloat(nftData.deer_config?.cost_multi).toFixed(2)}x
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#ddd",
                  textAlign: "center",
                  fontStyle: "italic",
                  mb: 0.5,
                  fontSize: "0.7rem",
                }}
              >
                Reward {parseFloat(rewardMultiplier).toFixed(2)}x
              </Typography>
            </Box>
            <Box
              sx={{ 
                display: "flex", 
                justifyContent: "space-around", 
                mt: 1,
                gap: 0.5
              }}
            >
              {onUpgrade && nftData.stakedConfig && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpgrade();
                  }}
                  variant="contained"
                  color="warning"
                  size="small"
                  sx={{ 
                    fontSize: "0.7rem", 
                    minWidth: "auto", 
                    px: 1,
                    py: 0.5,
                    lineHeight: 1.2,
                    opacity: parseInt(nftData.level) === 5 || getDeerStatus() === "Upgrading" ? 0.6 : 1,
                    color: "#fff",
                    backgroundColor: parseInt(nftData.level) === 5 ? "#bdbdbd" : undefined,
                    pointerEvents: parseInt(nftData.level) === 5 || getDeerStatus() === "Upgrading" ? "none" : "auto",
                  }}
                >
                  {getDeerStatus() === "Upgrading" &&  upgradeTime > 0
                      ? GetTimeString(upgradeTime)
                    : "Upgrade"}
                </Button>
              )}

              {onStake && !nftData.stakedConfig && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStake();
                  }}
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{ 
                    fontSize: "0.7rem", 
                    minWidth: "auto", 
                    px: 1,
                    py: 0.5,
                    lineHeight: 1.2
                  }}
                >
                  Stake
                </Button>
              )}
              <Button
                href={`https://wax.atomichub.io/explorer/asset/wax-mainnet/${assetID}`}
                target="_blank"
                rel="noreferrer"
                variant="contained"
                color="primary"
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{ 
                  fontSize: "0.7rem", 
                  minWidth: "auto", 
                  px: 1,
                  py: 0.5,
                  lineHeight: 1.2
                }}
              >
                View
              </Button>
            </Box>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default DeerCard;
